<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import moment from 'moment'
import IconBase from '../icon/IconBase.vue'
import IconChevronLeft from '../icon/IconChevronLeft.vue'
import IconChevronRight from '../icon/IconChevronRight.vue'
import IconChevronDown from '../icon/IconChevronDown.vue'
import IconCalendar from '../icon/IconCalendar.vue'
import FormButton from './FormButton.vue'

const props = defineProps({
  modelValue: {
    type: [Date, String, Number, null],
    default: null,
  },
  // Earliest selectable date-time; days/times before it are disabled.
  minDate: {
    type: [Date, String, Number, null],
    default: null,
  },
  // Latest selectable date; days after it are disabled and manual entry clamps to it.
  maxDate: {
    type: [Date, String, Number, null],
    default: null,
  },
  // Date-only mode: no time step/inputs. Selecting a day commits immediately and
  // the value is set to the end of that day (inclusive, e.g. for an expiry date).
  dateOnly: {
    type: Boolean,
    default: false,
  },
  // Trigger size: 'sm' (h-8, matches compact form inputs) or 'md' (h-10).
  size: {
    type: String,
    default: 'md',
  },
  // Heading shown above the hour/minute selectors in the time step.
  timeLabel: {
    type: String,
    default: 'Start time',
  },
  // When true, clicking anywhere on the trigger (not just the chevron) opens
  // the calendar. Manual typing still works. Off by default to preserve the
  // type-first behaviour used by other consumers.
  openOnClick: {
    type: Boolean,
    default: false,
  },
  // When true, on small screens the calendar renders in the normal flow
  // (pushing following content down) instead of as an absolute overlay, so it
  // can't overlap sibling fields inside a narrow container. Desktop keeps the
  // floating overlay.
  inlineOnMobile: {
    type: Boolean,
    default: false,
  },
  // Side the floating calendar hangs from: 'left' grows rightwards, 'right'
  // grows leftwards (for a field at the right edge of a narrow container).
  align: {
    type: String,
    default: 'left',
  },
})

const emit = defineEmits(['update:modelValue'])

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

const toDate = (v) => (v ? new Date(v) : null)
const startOfDay = (d) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}
const roundTo5 = (m) => {
  const r = Math.ceil(m / 5) * 5
  return r >= 60 ? 55 : r
}
const pad = (n) => String(n).padStart(2, '0')

// Internal state
const selectedDay = ref(null) // Date at 00:00, or null
const hour = ref(0)
const minute = ref(0)
const viewYear = ref(new Date().getFullYear())
const viewMonth = ref(new Date().getMonth())

// Manual text entry + calendar collapse
const INPUT_FORMAT = computed(() => (props.dateOnly ? 'MM/DD/YYYY' : 'MM/DD/YYYY HH:mm'))
const PARSE_FORMATS = computed(() => (props.dateOnly
  ? ['MM/DD/YYYY', 'M/D/YYYY', 'MM/DD/YY', 'YYYY-MM-DD']
  : ['MM/DD/YYYY HH:mm', 'M/D/YYYY H:mm', 'MM/DD/YY HH:mm', 'YYYY-MM-DD HH:mm', 'MM/DD/YYYY HH:mm:ss']))
const inputPlaceholder = computed(() => (props.dateOnly ? 'MM/DD/YYYY' : 'MM/DD/YYYY HH:MM'))
// Keep room for 16px text on mobile (iOS zoom fix); compact height from sm up.
const triggerHeightClass = computed(() => (props.size === 'sm' ? 'h-10 sm:h-8' : 'h-10'))
// In-flow on mobile (no overlap with siblings), floating overlay on desktop.
const calendarPositionClass = computed(() => [
  props.inlineOnMobile ? 'relative sm:absolute min-w-0 sm:min-w-[280px]' : 'absolute min-w-[280px]',
  props.align === 'right' ? 'left-0 sm:left-auto sm:right-0' : 'left-0',
].join(' '))
// text-base (16px) below the sm breakpoint avoids iOS zooming focused inputs;
// keep the compact desktop sizes from sm up.
const triggerTextClass = computed(() => (props.size === 'sm' ? 'text-base sm:text-tiny' : 'text-base sm:text-sm'))
const inputText = ref('')
const showCalendar = ref(false)
const step = ref('date') // 'date' | 'time'
// True while the text field is focused, so external re-syncs never clobber
// what the user is actively typing.
const isEditing = ref(false)
// Narrow viewport: open the picker on tap and skip the soft keyboard.
const isNarrow = ref(false)
let narrowMql = null
const syncNarrow = () => {
  isNarrow.value = !!narrowMql?.matches
}

const minDateObj = computed(() => (props.minDate ? new Date(props.minDate) : null))
const maxDateObj = computed(() => (props.maxDate ? new Date(props.maxDate) : null))

// Sync internal state from the bound value (e.g. presets set it externally).
const syncFromModel = () => {
  const v = toDate(props.modelValue)
  const base = v || minDateObj.value || new Date()
  if (v) {
    selectedDay.value = startOfDay(v)
    hour.value = v.getHours()
    minute.value = v.getMinutes() // preserve exact (manual) minutes, don't round
  } else {
    selectedDay.value = null
    hour.value = base.getHours()
    minute.value = roundTo5(base.getMinutes())
  }
  viewYear.value = (v || base).getFullYear()
  viewMonth.value = (v || base).getMonth()
  refreshInput()
}
watch(() => props.modelValue, syncFromModel)

// Apply the effective time to a day: end-of-day in date-only mode (so an expiry
// date includes the whole day), otherwise the selected hour/minute.
const applyTime = (d) => {
  if (props.dateOnly) d.setHours(23, 59, 59, 999)
  else d.setHours(Number(hour.value), Number(minute.value), 0, 0)
  return d
}

// Current selection as a Date (or null)
const currentDate = () => {
  if (!selectedDay.value) return null
  return applyTime(new Date(selectedDay.value))
}

// Write the formatted picker value into the text field.
const formatInput = () => {
  const d = currentDate()
  inputText.value = d ? moment(d).format(INPUT_FORMAT.value) : ''
}

// Keep the editable field in sync with the picker — but never overwrite text
// the user is actively typing.
const refreshInput = () => {
  if (isEditing.value) return
  formatInput()
}

// As the user types a complete, valid date-time, reflect it on the calendar and
// time controls AND apply it live — so manual entry works on its own, without
// opening the picker or clicking "Set". (Clamping to min happens on commit.)
const onInputTyping = () => {
  const text = (inputText.value || '').trim()
  if (!text) return
  const m = moment(text, PARSE_FORMATS.value, false)
  if (!m.isValid()) return
  const d = m.toDate()
  selectedDay.value = startOfDay(d)
  hour.value = d.getHours()
  minute.value = d.getMinutes()
  viewYear.value = d.getFullYear()
  viewMonth.value = d.getMonth()
  emitChange()
}

const inputRef = ref(null)
const focusInput = () => { inputRef.value?.focus() }
const onTriggerClick = () => {
  // On mobile, tap anywhere on the field to open the calendar — don't focus
  // the text input or iOS will raise the keyboard over the picker.
  if (isNarrow.value) {
    inputRef.value?.blur()
    if (!showCalendar.value) openCalendar()
    return
  }
  focusInput()
  if (props.openOnClick && !showCalendar.value) openCalendar()
}

const onInputFocus = () => {
  isEditing.value = true
  if (isNarrow.value && !showCalendar.value) openCalendar()
}
const onInputBlur = () => {
  isEditing.value = false
  commitInput()
}
const commitAndClose = () => {
  commitInput()
  showCalendar.value = false
}

// Parse a manual entry, validate against min, and apply it.
const commitInput = () => {
  const text = (inputText.value || '').trim()
  if (!text) {
    selectedDay.value = null
    emit('update:modelValue', null)
    return
  }
  const m = moment(text, PARSE_FORMATS.value, false)
  if (!m.isValid()) {
    formatInput() // revert to last valid value
    return
  }
  let d = m.toDate()
  const min = minDateObj.value
  if (min && d.getTime() < new Date(min).getTime()) {
    d = new Date(min) // can't schedule in the past — clamp to earliest allowed
  }
  const max = maxDateObj.value
  if (max && d.getTime() > new Date(max).getTime()) {
    d = new Date(max) // can't schedule beyond the allowed window — clamp to latest
  }
  selectedDay.value = startOfDay(d)
  hour.value = d.getHours()
  minute.value = d.getMinutes()
  viewYear.value = d.getFullYear()
  viewMonth.value = d.getMonth()
  emitChange()
  formatInput()
}

const minForSelectedDay = computed(() => {
  const min = minDateObj.value
  if (!min || !selectedDay.value) return null
  return startOfDay(min).getTime() === selectedDay.value.getTime()
    ? { hour: min.getHours(), minute: roundTo5(min.getMinutes()) }
    : null
})

const hourOptions = computed(() => {
  const out = []
  const minH = minForSelectedDay.value?.hour ?? 0
  for (let h = minH; h <= 23; h++) out.push(h)
  return out
})

const minuteOptions = computed(() => {
  const constraint = minForSelectedDay.value
  const minM = constraint && Number(hour.value) === constraint.hour ? constraint.minute : 0
  const set = new Set()
  for (let m = minM; m <= 55; m += 5) set.add(m)
  // Include a manually-typed minute (e.g. 09:17) so the dropdown can display it.
  const cur = Number(minute.value)
  if (cur >= minM) set.add(cur)
  return [...set].sort((a, b) => a - b)
})

const monthLabel = computed(() => `${MONTHS[viewMonth.value]} ${viewYear.value}`)
const selectedDateLabel = computed(() => (selectedDay.value ? moment(selectedDay.value).format('dddd, MMM D') : ''))

const days = computed(() => {
  const year = viewYear.value
  const month = viewMonth.value
  const first = new Date(year, month, 1)
  const startIdx = (first.getDay() + 6) % 7 // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const totalCells = Math.ceil((startIdx + daysInMonth) / 7) * 7 // trim trailing empty week
  const gridStart = new Date(year, month, 1 - startIdx)
  const min = minDateObj.value ? startOfDay(minDateObj.value) : null
  const max = maxDateObj.value ? startOfDay(maxDateObj.value) : null
  const today = startOfDay(new Date())
  const sel = selectedDay.value ? selectedDay.value.getTime() : null

  const cells = []
  for (let i = 0; i < totalCells; i++) {
    const d = new Date(gridStart)
    d.setDate(gridStart.getDate() + i)
    const day = startOfDay(d)
    cells.push({
      key: day.getTime(),
      date: d,
      day,
      label: d.getDate(),
      inMonth: d.getMonth() === month,
      isToday: day.getTime() === today.getTime(),
      isSelected: sel !== null && day.getTime() === sel,
      disabled: (min && day.getTime() < min.getTime()) || (max && day.getTime() > max.getTime()),
    })
  }
  return cells
})

const emitChange = () => {
  if (!selectedDay.value) {
    emit('update:modelValue', null)
    return
  }
  emit('update:modelValue', applyTime(new Date(selectedDay.value)))
}

const clampTimeToMin = () => {
  const constraint = minForSelectedDay.value
  if (!constraint) return
  if (Number(hour.value) < constraint.hour) {
    hour.value = constraint.hour
    minute.value = constraint.minute
  } else if (Number(hour.value) === constraint.hour && Number(minute.value) < constraint.minute) {
    minute.value = constraint.minute
  }
}

// Calendar/time edits are a draft — committed only via applyDraft().
const selectDay = (cell) => {
  if (cell.disabled) return
  selectedDay.value = cell.day
  if (!cell.inMonth) {
    viewYear.value = cell.date.getFullYear()
    viewMonth.value = cell.date.getMonth()
  }
  // Date-only: no time step — commit the date and close.
  if (props.dateOnly) {
    refreshInput()
    applyDraft()
    return
  }
  clampTimeToMin()
  refreshInput()
  step.value = 'time' // date chosen → move to time
}

const onTimeChange = () => {
  clampTimeToMin()
  refreshInput()
}

const goToDateStep = () => { step.value = 'date' }

const rootEl = ref(null)

const openCalendar = () => {
  // Always open on the calendar first (most intuitive), even when a date is
  // already set — the user can then step forward to the time picker.
  step.value = 'date'
  showCalendar.value = true
}
const applyDraft = () => {
  emitChange()
  showCalendar.value = false
}
const cancelDraft = () => {
  syncFromModel() // discard draft, restore committed value
  showCalendar.value = false
}
const toggleCalendar = () => {
  if (showCalendar.value) cancelDraft()
  else openCalendar()
}

// Close (and revert draft) on outside click / Escape.
const onDocMouseDown = (e) => {
  if (showCalendar.value && rootEl.value && !rootEl.value.contains(e.target)) {
    cancelDraft()
  }
}
// Captured and marked handled so an enclosing modal stays open.
const onDocKeydown = (e) => {
  if (!showCalendar.value || e.key !== 'Escape' || e.defaultPrevented) return
  e.preventDefault()
  cancelDraft()
}
onMounted(() => {
  narrowMql = window.matchMedia('(max-width: 639px)')
  syncNarrow()
  narrowMql.addEventListener('change', syncNarrow)
  document.addEventListener('mousedown', onDocMouseDown)
  document.addEventListener('keydown', onDocKeydown, true)
})
onBeforeUnmount(() => {
  narrowMql?.removeEventListener('change', syncNarrow)
  document.removeEventListener('mousedown', onDocMouseDown)
  document.removeEventListener('keydown', onDocKeydown, true)
})

const prevMonth = () => {
  if (viewMonth.value === 0) {
    viewMonth.value = 11
    viewYear.value -= 1
  } else {
    viewMonth.value -= 1
  }
}
const nextMonth = () => {
  if (viewMonth.value === 11) {
    viewMonth.value = 0
    viewYear.value += 1
  } else {
    viewMonth.value += 1
  }
}

const prevDisabled = computed(() => {
  const min = minDateObj.value
  if (!min) return false
  return viewYear.value < min.getFullYear() ||
    (viewYear.value === min.getFullYear() && viewMonth.value <= min.getMonth())
})

const nextDisabled = computed(() => {
  const max = maxDateObj.value
  if (!max) return false
  return viewYear.value > max.getFullYear() ||
    (viewYear.value === max.getFullYear() && viewMonth.value >= max.getMonth())
})

// Initial sync (deferred until all helpers above are defined)
syncFromModel()
</script>

<template>
  <div ref="rootEl" class="relative select-none">
    <!-- Trigger: editable value + calendar toggle -->
    <div @click="onTriggerClick"
      class="flex items-center gap-1.5 px-3 rounded-md bg-surface-3 hover:bg-surface-4 ring-1 transition-colors"
      :class="[
        showCalendar ? 'ring-main bg-surface-4' : 'ring-transparent',
        triggerHeightClass,
        isNarrow ? 'cursor-pointer' : 'cursor-text',
      ]">
      <icon-base class="shrink-0 text-surface-7" width="15" height="15">
        <icon-calendar />
      </icon-base>
      <input ref="inputRef" v-model="inputText" @focus="onInputFocus" @input="onInputTyping" @blur="onInputBlur"
        @keydown.enter.prevent="commitAndClose"
        :placeholder="inputPlaceholder" spellcheck="false"
        :readonly="isNarrow"
        class="flex-1 min-w-0 bg-transparent text-white placeholder:text-surface-6 focus:outline-none"
        :class="[triggerTextClass, isNarrow ? 'cursor-pointer' : 'cursor-text']" />
      <button type="button" @click.stop="toggleCalendar"
        :title="showCalendar ? 'Hide calendar' : 'Show calendar'"
        class="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-surface-7 hover:text-white transition-colors -mr-1.5">
        <icon-base fill="none" width="16" height="16" class="transition-transform"
          :class="showCalendar ? 'rotate-180' : ''">
          <icon-chevron-down />
        </icon-base>
      </button>
    </div>

    <!-- Floating calendar dropdown -->
    <Transition enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1" enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1">
      <div v-if="showCalendar"
        class="z-40 mt-1.5 w-full rounded-xl bg-surface-1 ring-1 ring-surface-3 shadow-[0_12px_30px_rgba(0,0,0,0.5)] p-3.5"
        :class="calendarPositionClass">

        <!-- STEP 1: pick a date -->
        <template v-if="step === 'date'">
          <!-- Month navigation -->
          <div class="flex items-center justify-between mb-3">
            <button type="button" :disabled="prevDisabled" @click="prevMonth"
              class="inline-flex size-7 items-center justify-center rounded-lg text-surface-7 hover:text-white hover:bg-surface-3 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-surface-7 transition-colors">
              <icon-base fill="none" width="16" height="16">
                <icon-chevron-left />
              </icon-base>
            </button>
            <span class="text-sm font-semibold text-white">{{ monthLabel }}</span>
            <button type="button" :disabled="nextDisabled" @click="nextMonth"
              class="inline-flex size-7 items-center justify-center rounded-lg text-surface-7 hover:text-white hover:bg-surface-3 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-surface-7 transition-colors">
              <icon-base fill="none" width="16" height="16">
                <icon-chevron-right />
              </icon-base>
            </button>
          </div>

          <!-- Weekday header -->
          <div class="grid grid-cols-7 mb-1">
            <span v-for="d in WEEKDAYS" :key="d"
              class="h-6 flex items-center justify-center text-[10px] font-semibold uppercase tracking-wider text-surface-6">{{ d }}</span>
          </div>

          <!-- Day grid -->
          <div class="grid grid-cols-7 gap-y-0.5">
            <div v-for="cell in days" :key="cell.key" class="flex items-center justify-center">
              <button type="button" :disabled="cell.disabled" @click="selectDay(cell)"
                class="size-9 flex items-center justify-center rounded-lg text-sm transition-colors"
                :class="[
                  cell.isSelected
                    ? 'bg-main text-white font-semibold shadow-[0_2px_8px_rgba(0,98,255,0.4)]'
                    : cell.disabled
                      ? 'text-surface-5 cursor-not-allowed'
                      : cell.inMonth
                        ? 'text-surface-8 hover:bg-surface-3 hover:text-white'
                        : 'text-surface-6 hover:bg-surface-3',
                  !cell.isSelected && cell.isToday ? 'ring-1 ring-inset ring-main/40 text-main font-semibold' : '',
                ]">
                {{ cell.label }}
              </button>
            </div>
          </div>

          <div class="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-surface-3">
            <span class="text-tiny text-surface-6">{{ dateOnly ? 'Pick a date' : 'Pick a date to set the time' }}</span>
            <FormButton type="secondary" size="sm" @click="cancelDraft">Cancel</FormButton>
          </div>
        </template>

        <!-- STEP 2: pick a time -->
        <template v-else>
          <!-- Back to date -->
          <button type="button" @click="goToDateStep"
            class="flex items-center gap-1.5 -ml-1 mb-3 pr-2 py-1 rounded-lg text-surface-8 hover:text-white transition-colors">
            <icon-base fill="none" width="16" height="16">
              <icon-chevron-left />
            </icon-base>
            <span class="text-sm font-medium">{{ selectedDateLabel }}</span>
          </button>

          <!-- Time selection -->
          <div class="flex flex-col items-center gap-2 py-2">
            <span class="text-tiny font-medium text-surface-7 uppercase tracking-wider">{{ timeLabel }}</span>
            <div class="flex items-center gap-2">
              <div class="relative">
                <select v-model.number="hour" @change="onTimeChange" style="color-scheme: dark"
                  class="appearance-none h-11 w-16 rounded-lg bg-surface-3 hover:bg-surface-4 text-white text-lg font-medium text-center pl-3 pr-6 border-none focus:outline-none focus:ring-1 focus:ring-main transition-colors cursor-pointer">
                  <option v-for="h in hourOptions" :key="h" :value="h">{{ pad(h) }}</option>
                </select>
                <icon-base class="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-surface-7" width="12" height="12">
                  <icon-chevron-down />
                </icon-base>
              </div>
              <span class="text-lg text-surface-6 font-semibold">:</span>
              <div class="relative">
                <select v-model.number="minute" @change="onTimeChange" style="color-scheme: dark"
                  class="appearance-none h-11 w-16 rounded-lg bg-surface-3 hover:bg-surface-4 text-white text-lg font-medium text-center pl-3 pr-6 border-none focus:outline-none focus:ring-1 focus:ring-main transition-colors cursor-pointer">
                  <option v-for="m in minuteOptions" :key="m" :value="m">{{ pad(m) }}</option>
                </select>
                <icon-base class="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-surface-7" width="12" height="12">
                  <icon-chevron-down />
                </icon-base>
              </div>
            </div>
          </div>

          <!-- Confirm / discard -->
          <div class="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-surface-3">
            <FormButton type="secondary" size="sm" @click="cancelDraft">Cancel</FormButton>
            <FormButton type="main" size="sm" :disabled="!selectedDay" @click="applyDraft">Set date &amp; time</FormButton>
          </div>
        </template>
      </div>
    </Transition>
  </div>
</template>

<style scoped></style>
